import { createRoot } from "react-dom/client";
import { HelmetProvider } from "react-helmet-async";
import "./i18n/config";
import App from "./App.tsx";
import "./index.css";
import { registerServiceWorker } from "./lib/registerSW";
import { initGA } from "./lib/analytics";
import { runCacheBuster } from "./utils/cacheBuster";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { isBR } from "./lib/market";
import { startPtBRDom } from "./lib/ptBRDom";
import { installAdminMarketHeader } from "./lib/adminMarketHeader";
import { getAdminMarket } from "./hooks/useAdminMarket";
if (isBR) startPtBRDom();
installAdminMarketHeader(getAdminMarket, import.meta.env.VITE_SUPABASE_URL);

// Force reset client storage after major changes
runCacheBuster();
document.documentElement.dataset.reactEntry = "loaded";

// Auto-recover from stale chunk errors after a deploy
const RELOAD_KEY = "chunk_reload_ts";
const RELOAD_COOLDOWN_MS = 10_000;

const isChunkError = (msg: string) =>
  msg.includes("Failed to fetch dynamically imported module") ||
  msg.includes("Importing a module script failed") ||
  msg.includes("error loading dynamically imported module");

const handleChunkError = async () => {
  const last = Number(sessionStorage.getItem(RELOAD_KEY) || 0);
  if (Date.now() - last < RELOAD_COOLDOWN_MS) return;
  sessionStorage.setItem(RELOAD_KEY, String(Date.now()));

  // Purge SW caches and unregister so we fetch fresh chunks
  try {
    if ("caches" in window) {
      const keys = await caches.keys();
      await Promise.all(keys.map((k) => caches.delete(k)));
    }
    if ("serviceWorker" in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister()));
    }
  } catch {
    /* ignore */
  }
  window.location.reload();
};

window.addEventListener("error", (e) => {
  if (isChunkError(String(e?.message || ""))) handleChunkError();
});
window.addEventListener("unhandledrejection", (e) => {
  if (isChunkError(String(e?.reason?.message || ""))) handleChunkError();
});

createRoot(document.getElementById("root")!).render(
  <ErrorBoundary>
    <HelmetProvider>
      <App />
    </HelmetProvider>
  </ErrorBoundary>
);

// Register service worker after page is interactive
registerServiceWorker();

// Initialize Google Analytics
initGA();
